import { Fragment, useState } from 'react'
import { atom, type PrimitiveAtom, useAtom } from 'jotai'
import Box from '@mui/material/Box'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import dayjs from 'dayjs'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'

import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import {
    UserRole,
    type Pagination,
} from '@/types'
import PaginationControls from '@/components/pagination-controls'
import { useUsers } from '@/hooks/api/use-users'
import RoleSelector from './role-selector'
import { USER_ROLE_LABEL_MAP } from '@/constants/general'
import UserDetail from './user-detail'
import UserForm from '@/components/forms/user'
import PermissionsGate from '@/components/permissions-gate'
import MunicipalitySelector from './municipality-selector'
import SearchField from '@/components/filters/search-field'
import CollectionCompanySelector from './collection-company-selector'

// Filters
const rolesAtom = atom<UserRole[]>([])
const municipalityIdAtom = atom<string>('')
const collectionCompanyIdAtom = atom<string>('')
const emailLikeAtom = atom<string>('')
const emailLikeValueAtom = atom<string>('')

const paginationAtom = atom<Pagination>({
    limit: 20,
    offset: 0,
})

const userRolesWithoutCPAdmin = Object.values(UserRole).filter(role => role !== UserRole.collectionPointAdmin)

const selectedUserIdAtom = atom<number | null>(null) as PrimitiveAtom<number | null>

const MAX_COLLECTION_POINTS = 1

function UserList({
    showRoles,
}: {
    showRoles: 'collectionPointAdmin' | 'other',
}) {

    const [selectedUserId, setSelectedUserId] = useAtom(selectedUserIdAtom)
    const [create, setCreate] = useState<boolean>(false)

    // Pagination
    const [pagination, setPagination] = useAtom(paginationAtom)

    const handleResetPagination = () => {
        setPagination({
            limit: 20,
            offset: 0,
        })
    }

    // Filters
    const [roles, setRoles] = useAtom(rolesAtom)
    const [municipalityId, setMunicipalityId] = useAtom(municipalityIdAtom)
    const [collectionCompanyId, setCollectionCompanyId] = useAtom(collectionCompanyIdAtom)
    const [emailLike, setEmailLike] = useAtom(emailLikeAtom)
    const [emailLikeValue, setEmailLikeValue] = useAtom(emailLikeValueAtom)

    const handleResetFilters = () => {
        setRoles([])
        setMunicipalityId('')
        setCollectionCompanyId('')
        setEmailLike('')
        setEmailLikeValue('')
    }
    
    const { status, data, error } = useUsers(pagination, {
        roles: showRoles === 'other' ?
            roles.length === 0 ? userRolesWithoutCPAdmin : roles
            :
            [UserRole.collectionPointAdmin],
        municipalityId,
        collectionCompanyId,
        emailLike,
    })

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={'Nepovedlo se načíst seznam uživatelů'}
        />
    }

    const selectedUser = data.user.find(user => user.id === selectedUserId)

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto'
        }}>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                px: 2.5,
                gap: 1.5,
                pb: 2,
                pt: 1,
                alignItems: 'center',
            }}>
                <FilterAltRoundedIcon />
                <Box>
                    <SearchField
                        setValue={setEmailLike}
                        inputValue={emailLikeValue}
                        setInputValue={setEmailLikeValue}
                        label="Hledat dle emailu"
                    />
                </Box>
                {showRoles === 'other' &&
                    <Box display="flex">
                        <RoleSelector
                            label="Role"
                            roles={roles}
                            setRoles={setRoles}
                            callback={handleResetPagination}
                        />
                    </Box>
                }
                <Box display="flex">
                    <CollectionCompanySelector
                        collectionCompanyId={collectionCompanyId}
                        setCollectionCompanyId={setCollectionCompanyId}
                    />
                </Box>
                <Box display="flex">
                    <MunicipalitySelector
                        municipalityId={municipalityId}
                        setMunicipalityId={setMunicipalityId}
                    />
                </Box>
                <Tooltip title="Resetovat filtry">
                    <IconButton
                        aria-label="Resetovat filtry"
                        onClick={() => handleResetFilters()}
                        size="small"
                    >
                        <DoNotDisturbAltRoundedIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{
                overflowX: 'auto',
            }}>
                {data.records_count === 0 ?
                    <Box px={2.5} pb={2.5}>
                        Seznam je prázdný
                    </Box>
                :
                    <Box>
                        <Table
                            size="small"
                            aria-label="Seznam uživatelů"
                            sx={{
                                mt: 1,
                                minWidth: 600,
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ paddingLeft: 3 }}>Aktivní</TableCell>
                                    <TableCell>Email</TableCell>
                                    {showRoles === 'other' &&
                                        <TableCell>Role</TableCell>
                                    }
                                    <TableCell>{showRoles === 'other' ? 'Obec' : 'Svozová místa'}</TableCell>
                                    {showRoles === 'other' &&
                                        <TableCell>Svoz. společnost</TableCell>
                                    }
                                    <TableCell>EOKO ID kontaktu</TableCell>
                                    <TableCell>Dat. schválení</TableCell>
                                    {showRoles === 'collectionPointAdmin' &&
                                        <TableCell>Dat. odeslání přístupu</TableCell>
                                    }
                                    <TableCell align="right"/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.user.map(row => (
                                    <Fragment key={row.id}>
                                        <TableRow
                                            key={row.id}
                                            sx={(theme) => ({
                                                cursor: 'pointer',
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '& > td, th': { borderBottom: 'unset' },
                                                ...(row.id === selectedUserId ? {
                                                    backgroundColor: theme.palette.customLightYellow.light,
                                                } : {})
                                            })}
                                            onClick={() => setSelectedUserId(row.id)}
                                            hover
                                        >
                                            <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                                <Box p={1.5}>
                                                    {row.active ?
                                                        <Box sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: 'success.light',
                                                        }}/>
                                                        :
                                                        <Box sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: 'error.light',
                                                        }}/>
                                                    }
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {row.email}
                                            </TableCell>
                                            {showRoles === 'other' &&
                                                <TableCell>
                                                    {USER_ROLE_LABEL_MAP[row.role]}
                                                </TableCell>
                                            }
                                            <TableCell>
                                                {row.role === UserRole.collectionPointAdmin ?
                                                   <Box display="flex" flexWrap="wrap" gap={1}>
                                                        {(() => {
                                                            const households = row.eoko_contacts_users_junctions.flatMap(junction =>
                                                                junction.eoko_contact?.households?.flatMap(household => {
                                                                    if (!household) return []
                                                                    const sameName = household.municipality?.name === household.address?.street
                                                                    return [{
                                                                        id: household.id,
                                                                        label: sameName
                                                                            ? `${household.address?.street} ${household.address?.street_number}`
                                                                            : `${household.municipality?.name} - ${household.address?.street} ${household.address?.street_number}`
                                                                    }]
                                                                }) ?? []
                                                            )
                                                        
                                                            const visibleHouseholds = households.slice(0, MAX_COLLECTION_POINTS)
                                                            const hiddenHouseholds = households.slice(MAX_COLLECTION_POINTS)
                                                            
                                                            return (
                                                                <>
                                                                    {visibleHouseholds.map(household => (
                                                                        <Chip
                                                                            key={household.id}
                                                                            label={household.label}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: 'customLightGreen.main'
                                                                            }}
                                                                        />
                                                                    ))}
                                                                    {hiddenHouseholds.length > 0 &&
                                                                        <Chip
                                                                            label={`+${hiddenHouseholds.length} dalších`}
                                                                            size="small"
                                                                        />
                                                                    }
                                                                </>
                                                            )
                                                        })()}
                                                    </Box>
                                                    :
                                                    row.municipality ?
                                                        row.municipality.name
                                                    :
                                                    '-'
                                                }
                                            </TableCell>
                                            {showRoles === 'other' &&
                                                <TableCell>
                                                    {row.collection_company?.eoko_customer?.name ?
                                                        row.collection_company.eoko_customer.name
                                                    :
                                                    '-'}
                                                </TableCell>
                                            }
                                            <TableCell>
                                            {row.eoko_contacts_users_junctions.length > 0 ? (() => {
                                                const contactIds = row.eoko_contacts_users_junctions.map(junction => junction.eoko_contact_id)
                                                const visibleContacts = contactIds.slice(0, MAX_COLLECTION_POINTS)
                                                const hiddenCount = contactIds.length - visibleContacts.length

                                                return hiddenCount > 0 
                                                    ? `${visibleContacts.join(', ')} (+${hiddenCount})`
                                                    : visibleContacts.join(', ')
                                            })() : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.approved_at ? dayjs(row.approved_at).format('D. M. YYYY HH:mm') : '-'}
                                            </TableCell>
                                            {showRoles === 'collectionPointAdmin' &&
                                                <TableCell>
                                                    {row.welcome_email_sent_at ? dayjs(row.welcome_email_sent_at).format('D. M. YYYY HH:mm') : '-'}
                                                </TableCell>
                                            }
                                            <TableCell align="right">
                                                <IconButton
                                                    id="user-settings-button"
                                                    aria-label="settings"
                                                    color="terciary"
                                                >
                                                    <SettingsRoundedIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ padding: 0 }} colSpan={11}>
                                                <Collapse in={row.id === selectedUserId} timeout="auto" unmountOnExit>
                                                    <Box sx={(theme) => ({
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        justifyContent: 'center',
                                                        borderTop: `1px solid ${theme.palette.customGrey.divider}`
                                                    })}>
                                                        {row.id === selectedUserId &&
                                                            <Fade in={!!selectedUserId} style={{ transitionDelay: selectedUserId ? '200ms' : '0ms', width: '100%' }}>
                                                                <Box>
                                                                    <UserDetail
                                                                        user={selectedUser}
                                                                        handleClose={() => setSelectedUserId(null)}
                                                                    />
                                                                </Box>
                                                            </Fade>
                                                        }
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                ))}
                            </TableBody>
                        </Table>
                        <PaginationControls
                            pagination={pagination}
                            setPagination={setPagination}
                            currentArrayLength={data.user.length}
                            recordsCount={data.records_count}
                        />
                    </Box>
                }
                {showRoles === 'other' &&
                    <PermissionsGate
                        userRoles={[
                            UserRole.admin,
                            UserRole.municipalityOfficial,
                            UserRole.eokoDataAdmin,
                            UserRole.collectionCompany,
                        ]}
                    >
                        <Box px={2.5} pb={1.5}>
                            {create ?
                                <Box maxWidth="sm">
                                    <UserForm
                                        closeCallback={() => setCreate(false)}
                                    />
                                </Box>
                                :
                                <Button
                                    variant="contained"
                                    color="terciary"
                                    startIcon={<AddRoundedIcon />}
                                    onClick={() => setCreate(true)}
                                >
                                    Vytvořit uživatele
                                </Button>
                            }
                        </Box>
                    </PermissionsGate>
                }
            </Box>
        </Box>
    )
}

export default UserList
